import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

/**
 * Reusable SEO head — sets <title>, meta description, canonical, hreflang, and OpenGraph.
 *
 * Usage:
 *   <SEO titleKey="seo.home.title" descriptionKey="seo.home.description" />
 *
 * Titles & descriptions live in locale JSON under the `seo` namespace,
 * falling back to sensible defaults if a key is missing.
 */

const SITE_URL = 'https://flexi-location.be';
const SITE_NAME = 'Flexi-location';
const DEFAULT_OG_IMAGE = 'https://customer-assets.emergentagent.com/job_instant-access-space/artifacts/jtfp03jx_Flexic.jpg';
const SUPPORTED_LANGS = ['fr', 'nl', 'en'];

export default function SEO({ titleKey, descriptionKey, image, noIndex = false }) {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const lang = (i18n.language || 'fr').slice(0, 2).toLowerCase();
  const path = location.pathname.replace(/\/+$/, '') || '';
  const canonical = `${SITE_URL}${path || '/'}`;

  const title = titleKey ? t(titleKey, { defaultValue: SITE_NAME }) : SITE_NAME;
  const description = descriptionKey
    ? t(descriptionKey, { defaultValue: '' })
    : '';
  const fullTitle = title === SITE_NAME ? title : `${title} | ${SITE_NAME}`;
  const ogImage = image || DEFAULT_OG_IMAGE;

  return (
    <Helmet>
      <html lang={lang} />
      <title>{fullTitle}</title>
      {description && <meta name="description" content={description} />}
      <link rel="canonical" href={canonical} />

      {/* hreflang alternates — Google uses these for multilingual indexing */}
      {SUPPORTED_LANGS.map(l => (
        <link
          key={l}
          rel="alternate"
          hrefLang={l}
          href={`${SITE_URL}${path || '/'}?lang=${l}`}
        />
      ))}
      <link rel="alternate" hrefLang="x-default" href={`${SITE_URL}${path || '/'}`} />

      {/* OpenGraph */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      {description && <meta property="og:description" content={description} />}
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:locale" content={lang === 'fr' ? 'fr_BE' : lang === 'nl' ? 'nl_BE' : 'en_US'} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      {description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:image" content={ogImage} />

      {noIndex && <meta name="robots" content="noindex, nofollow" />}
    </Helmet>
  );
}
